import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from "react";
import api, { BASE_URL } from "../../Utils/api";
import DocumentosModal from "./DocumentosModal";
import GridPadrao from "../GridPadrao/GridPadrao";
import { Modal, Button, Badge, Spinner, Form, Pagination } from "react-bootstrap";
import formatarData from "../../Utils/formatarData";
import calcDiasParaVcto from "../../Utils/calcDiasParaVcto";

import { getDescricaoPeriodicidade } from '../../Utils/dadosAgendamento';
import { GlobalContext } from "../../Utils/GlobalContext";
import { useFeedback } from "../Generals/Feedback/FeedbackContext";
import PageHeader from "../PageHeader/PageHeader";
import gerarDocumentoPdf from "../../Utils/gerarDocumentoPdf";
import { podeAdicionarDocumentoAtivo } from "../../Utils/verificarLimiteDocumentosAtivos";
import "../../App.css";

const TIMEOUT_DOCUMENTOS_MS = 20000;


/**
 * Achata campos_customizados no documento para a grid: cada item vira doc[nome_campo] = valor.
 * ListarDocumentos retorna documentos com array campos_customizados (nome_campo, valor_texto,
 * valor_numero, valor_data, id_opcao, identifica_pessoa).
 *
 * Item #36 (LGPD): campos customizados marcados como "Dado pessoal identificável" também
 * populam `camposParaMascarar` (mutado como acumulador) com `nome_campo -> "observacoes"`
 * (tipo de mascaramento — sempre oculta por completo, igual a um campo de texto livre),
 * para montar exportMaskColumns dinamicamente (colunas variam por tipo de documento).
 */
function documentoComCamposCustomizadosFlat(doc, camposParaMascarar) {
  if (!doc) return doc;
  const flat = { ...doc };
  const customizados = doc.campos_customizados;
  if (Array.isArray(customizados) && customizados.length > 0) {
    customizados.forEach((c) => {
      const valor =
        c.valor_texto ??
        (c.valor_numero != null ? c.valor_numero : null) ??
        (c.valor_data ? formatarData(c.valor_data) : null) ??
        (c.id_opcao != null ? String(c.id_opcao) : null) ??
        "";
      flat[c.nome_campo] = valor;
      if (camposParaMascarar && c.identifica_pessoa === "S") {
        camposParaMascarar[c.nome_campo] = "observacoes";
      }
    });
  }
  return flat;
}

function Documentos() {
  const [documentos, setDocumentos] = useState([]);
  const [totalDocumentos, setTotalDocumentos] = useState(0);
  const [loadingDocumentos, setLoadingDocumentos] = useState(true);
  const [selectedDocumento, setSelectedDocumento] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAgendamentoModal, setShowAgendamentoModal] = useState(false);
  const [agendamentosDetalhes, setAgendamentosDetalhes] = useState([]);
  const [loadingAgendamentos, setLoadingAgendamentos] = useState(false);
  const [pendentesOnly, setPendentesOnly] = useState(true);
  const [agendamentosPage, setAgendamentosPage] = useState(1);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewHtml, setPdfPreviewHtml] = useState(null);
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  // Item #36 (LGPD): colunas de campos customizados marcadas como "Dado pessoal
  // identificável", montadas dinamicamente a cada fetch (variam por tipo de documento).
  const [exportMaskColumnsCustomizados, setExportMaskColumnsCustomizados] = useState({});
  const pdfIframeRef = React.useRef(null);
  const lastFetchParamsRef = useRef(null);
  const hasDocumentosRef = useRef(false);

  const defaultGridFilters = useMemo(
    () => ({ filters: { situacao: "V" }, dateFilters: {} }),
    []
  );

  const token = localStorage.getItem("authToken");
  const { globalData } = useContext(GlobalContext);
  const { toast } = useFeedback();
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");
  // Item #36 (LGPD): fail-safe — antes da config carregar (null) ou com o switch
  // ativo, assume oculto.
  const lgpdAtivo = globalData?.lgpdOcultarDados !== false;
  const exportMaskColumns = lgpdAtivo ? exportMaskColumnsCustomizados : {};
  const userId = globalData?.user_id || localStorage.getItem("user_id");
  const numeroLinhasGrid = Number(globalData?.numero_linhas_grid) || 10;

  const handleEdit = (documento) => {
    setSelectedDocumento(documento);
    setShowModal(true); 
  };

  const handleAdd = async () => {
    const pode = await podeAdicionarDocumentoAtivo({
      baseUrl: BASE_URL,
      token,
      entidadeID,
      toast,
    });
    if (!pode) return;

    setSelectedDocumento(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedDocumento(null);
    fetchDocumentos(lastFetchParamsRef.current);
  };

  const handleExcluir = async (documento) => {
    if (window.confirm(`Tem certeza que deseja excluir o documento "${documento.nr_contrato || documento.id}"?`)) {
      try {
        await api.delete(
          `${BASE_URL}/api/documentos/ExcluirDocumento/${documento.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchDocumentos(lastFetchParamsRef.current);
      } catch (error) {
        console.error("Erro ao excluir documento:", error);
        alert(error.response?.data?.message || "Erro ao excluir documento. Tente novamente.");
      }
    }
  };

  const handleOpenAgendamento = async (documentoId) => {
    setAgendamentosDetalhes([]);
    setPendentesOnly(true);
    setAgendamentosPage(1);
    setShowAgendamentoModal(true);
    setLoadingAgendamentos(true);
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/agendamentos/ListarAgendamentosDocumentoPorID/${documentoId}`
      );
      const agrupado = data.data.reduce((acc, item) => {
        const key = `${item.nome}||${item.periodicidade}||${item.data_proxima_execucao || ""}||${item.situacao || ""}`;
        if (!acc[key]) {
          acc[key] = {
            nome: item.nome,
            periodicidade: item.periodicidade,
            data_proxima_execucao: item.data_proxima_execucao || null,
            situacao: item.situacao,
            emails: [],
          };
        }
        if (item.usuario?.email && !acc[key].emails.includes(item.usuario.email)) {
          acc[key].emails.push(item.usuario.email);
        }
        return acc;
      }, {});
      setAgendamentosDetalhes(Object.values(agrupado));
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
      setAgendamentosDetalhes([]);
    } finally {
      setLoadingAgendamentos(false);
    }
  };

  const handlePrintPdf = async (doc) => {
    setPdfPreviewLoading(true);
    setPdfPreviewHtml(null);
    try {
      const html = await gerarDocumentoPdf(doc, {
        nomeEntidade: globalData?.nome_entidade ?? null,
        logotipo: globalData?.logotipo ?? null,
        usuario: globalData?.user ?? null,
      });

      if (!html) {
        alert("Sem dados para exibir!");
        return;
      }

      setPdfPreviewHtml(html);
      setShowPdfPreview(true);
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  /** Colunas da grid. accessor deve coincidir com nome_coluna do ObterVisaoColunas para ordem/visibilidade. */
  const columns = [
    { Header: "ID", accessor: "id" },
    {
      Header: "Tipo Documento",
      accessor: "nome_tipo_documento",
      getFilterValue: (row) => row.nome_tipo_documento || row.tipo_documento,
      Cell: ({ row }) => row.nome_tipo_documento || row.tipo_documento || "—"
    },
    {
      Header: "Empresa",
      accessor: "empresa",
      getFilterValue: (row) => row.empresa || row.fantasia,
      Cell: ({ row }) => row.empresa || row.fantasia || "Indefinido"
    },
    {
      Header: "Fornecedor",
      accessor: "fornecedor",
      Cell: ({ row }) => row.fornecedor || "Indefinido"
    },
    {
      Header: "Número/Referência",
      accessor: "nr_contrato",
      Cell: ({ row }) => row.nr_contrato ?? "—"
    },
    {
      Header: "Data Inicial",
      accessor: "dt_inicial",
      type: "date",
      Cell: ({ row }) => (row.dt_inicial ? formatarData(row.dt_inicial) : "—")
    },
    {
      Header: "Data de Vencimento",
      accessor: "dt_vencimento",
      type: "date",
      Cell: ({ row }) => (row.dt_vencimento ? formatarData(row.dt_vencimento) : "—")
    },
    {
      Header: "Vence em",
      accessor: "dias_para_vencimento",
      Cell: ({ row }) => {
        const dias = calcDiasParaVcto(row.dt_vencimento);
        if (dias === null || dias === undefined) return "—";
        const variant = dias < 0 ? "danger" : dias <= 30 ? "warning" : "success";
        return (
          <Badge bg={variant}>
            {dias} dias
          </Badge>
        );
      }
    },
    {
      Header: "Dias de Aviso",
      accessor: "nr_dias_aviso",
      Cell: ({ row }) => {
        const dias = row.nr_dias_aviso != null ? row.nr_dias_aviso : calcDiasParaVcto(row.dt_vencimento);
        if (dias === null || dias === undefined) return "—";
        const variant = dias < 0 ? "danger" : dias <= 30 ? "warning" : "success";
        return (
          <Badge bg={variant}>
            {dias} dias
          </Badge>
        );
      }
    },
    {
      Header: "Agendamentos",
      accessor: "agendamentos",
      hideFilter: true,
      Cell: ({ row }) => {
        const total = Number(row.total_agendamentos_pendentes) || 0;
        return (
          <div
            className="agendamento-badge"
            onClick={() => handleOpenAgendamento(row.id)}
            title="Clique para ver agendamentos"
            role="button"
          >
            <i className="far fa-clock"></i>
            {total > 0 && (
              <span className="badge rounded-pill bg-primary ms-1">{total}</span>
            )}
          </div>
        );
      }
    },
    {
      Header: "Funcionário",
      accessor: "funcionario_nome",
      Cell: ({ row }) => row.funcionario_nome || "—"
    },
    {
      Header: "Responsável",
      accessor: "usuario_responsavel",
      Cell: ({ row }) => row.usuario_responsavel || "—"
    },
    {
      Header: "Tipo de Pagamento",
      accessor: "tipo_pagamento",
      Cell: ({ row }) => row.tipo_pagamento || "—"
    },
    {
      Header: "Índice de Correção",
      accessor: "indice_correcao",
      Cell: ({ row }) => row.indice_correcao || "—"
    },
    {
      Header: "Situação",
      accessor: "situacao",
      Cell: ({ row }) => {
        const s = row.situacao;
        if (s === "V") return "Vigente";
        if (s === "A") return "Arquivado";
        return s || "—";
      }
    }
  ];

  const fetchDocumentos = useCallback(async (params) => {
    if (!entidadeID || !userId) {
      console.warn("[Documentos] entidadeID ou userId ausente, não buscando documentos.");
      setDocumentos([]);
      setTotalDocumentos(0);
      hasDocumentosRef.current = false;
      setLoadingDocumentos(false);
      return;
    }

    const fetchParams = params || lastFetchParamsRef.current || {
      pagina: 1,
      limite: numeroLinhasGrid,
      ordenar: "dt_vencimento",
      direcao: "ASC",
      filters: { situacao: "V" },
    };
    lastFetchParamsRef.current = fetchParams;
    if (!hasDocumentosRef.current) {
      setLoadingDocumentos(true);
    }
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_DOCUMENTOS_MS);

    try {
      const response = await api.get(`${BASE_URL}/api/documentos/ListarDocumentos/${entidadeID}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: Number(userId) || userId,
          pagina: fetchParams.pagina,
          limite: fetchParams.limite,
          ordenar: fetchParams.ordenar,
          direcao: fetchParams.direcao,
          situacao: fetchParams.filters?.situacao || undefined,
        },
        signal: controller.signal,
      });

      const lista = Array.isArray(response.data?.data) ? response.data.data : [];
      const camposParaMascarar = {};
      const docsComCustom = lista.map((doc) => documentoComCamposCustomizadosFlat(doc, camposParaMascarar));
      setDocumentos(docsComCustom);
      setExportMaskColumnsCustomizados(camposParaMascarar);
      hasDocumentosRef.current = docsComCustom.length > 0;
      setTotalDocumentos(response.data?.paginacao?.total ?? docsComCustom.length);
    } catch (error) {
      console.error("Erro ao buscar documentos:", error);
      const abortado = error?.name === "CanceledError" || error?.code === "ERR_CANCELED";
      const msg = error?.response?.data?.message || error?.message || "Erro ao buscar documentos.";
      if (abortado || error?.response?.status === 400) {
        console.warn("[Documentos] Requisição rejeitada (400):", msg);
        setDocumentos([]);
        setTotalDocumentos(0);
        hasDocumentosRef.current = false;
      }
    } finally {
      clearTimeout(timeoutId);
      setLoadingDocumentos(false);
    }
  }, [entidadeID, token, userId, numeroLinhasGrid]);

  const handleServerParamsChange = useCallback((params) => {
    fetchDocumentos(params);
  }, [fetchDocumentos]);

  useEffect(() => {
    hasDocumentosRef.current = false;
    lastFetchParamsRef.current = null;
  }, [entidadeID, userId]);

  useEffect(() => {
    if (!entidadeID || !userId) return;

    const fetchTiposAgendamento = async () => {
      try {
        await api.get(`${BASE_URL}/api/tiposAgendamentos/ListarTiposAgendamento/${entidadeID}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error("Erro ao buscar tipos de agendamento:", err);
      }
    };

    fetchTiposAgendamento();
  }, [entidadeID, token, userId]);

  return (
    <div>
      <PageHeader titulo="Cadastro de Documentos" path="/Documentos" />

      <GridPadrao
        columns={columns}
        data={documentos}
        onEdit={handleEdit}
        onAdd={handleAdd}
        onInactivate={handleExcluir}
        onPrintPdf={handlePrintPdf}
        defaultSortColumn="dt_vencimento"
        defaultSortOrder="asc"
        rota="Documentos"
        defaultFilters={defaultGridFilters}
        situacaoFilterOptions={[
          { value: "V", label: "Vigente" },
          { value: "A", label: "Arquivado" },
        ]}
        serverSide
        totalItems={totalDocumentos}
        loading={loadingDocumentos}
        onServerParamsChange={handleServerParamsChange}
        exportMaskColumns={exportMaskColumns}
      />

      {showModal && (
        <DocumentosModal
          show={showModal}
          handleClose={handleClose}
          documento={selectedDocumento}
          fetchDocumentos={fetchDocumentos}
        />
      )}

      {/* Modal de pré-visualização PDF */}
      <Modal
        show={showPdfPreview}
        onHide={() => setShowPdfPreview(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Pré Visualização PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ minHeight: "70vh" }}>
          {pdfPreviewLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" />
            </div>
          ) : pdfPreviewHtml ? (
            <iframe
              ref={pdfIframeRef}
              title="Pré Visualização PDF"
              srcDoc={pdfPreviewHtml}
              style={{ width: "100%", height: "70vh", border: "none" }}
            />
          ) : (
            <div className="text-center text-muted py-5">Nenhum conteúdo para exibir.</div>
          )}
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button
              variant="success"
              className="modal-footer-btn-salvar"
              onClick={() => pdfIframeRef.current?.contentWindow?.print()}
              disabled={!pdfPreviewHtml}
            >
              Imprimir
            </Button>
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setShowPdfPreview(false)}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal: Agendamentos Configurados */}
      <Modal
        show={showAgendamentoModal}
        onHide={() => setShowAgendamentoModal(false)}
        size="xl"
        dialogClassName="modal-dialog-top"
      >
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-3">
            <Modal.Title>Agendamentos Configurados</Modal.Title>
            {!loadingAgendamentos && agendamentosDetalhes.length > 0 && (
              <Form.Check
                type="checkbox"
                id="filtro-pendentes-grid"
                label="Pendentes"
                checked={pendentesOnly}
                onChange={(e) => { setPendentesOnly(e.target.checked); setAgendamentosPage(1); }}
                className="mb-0"
              />
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          {loadingAgendamentos ? (
            <div className="d-flex justify-content-center align-items-center py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Carregando agendamentos...</span>
            </div>
          ) : agendamentosDetalhes.length === 0 ? (
            <div className="text-center p-4">
              <i className="far fa-clock fa-3x mb-3 text-muted"></i>
              <p>Nenhum agendamento gerado para este documento.</p>
              <p className="text-muted small">
                Acesse a tela de <strong>Agendamentos</strong> e clique em <strong>Gerar Agendamentos</strong>.
              </p>
            </div>
          ) : (() => {
            const agFiltrados = agendamentosDetalhes
              .filter((r) => !pendentesOnly || r.situacao === "P")
              .sort((a, b) => {
                const da = a.data_proxima_execucao ? new Date(a.data_proxima_execucao).getTime() : 0;
                const db = b.data_proxima_execucao ? new Date(b.data_proxima_execucao).getTime() : 0;
                return db - da;
              });
            const PAGE_SIZE = 10;
            const totalPages = Math.max(1, Math.ceil(agFiltrados.length / PAGE_SIZE));
            const pagina = agFiltrados.slice(
              (agendamentosPage - 1) * PAGE_SIZE,
              agendamentosPage * PAGE_SIZE
            );
            return (
              <>
                <table className="table table-striped table-sm">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Periodicidade</th>
                      <th>Data Execução</th>
                      <th>Situação</th>
                      <th>Emails</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagina.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-3">
                          Nenhum agendamento pendente.
                        </td>
                      </tr>
                    ) : pagina.map((row, idx) => {
                      let dataExecucao = "—";
                      if (row.data_proxima_execucao) {
                        try {
                          dataExecucao = new Date(row.data_proxima_execucao).toLocaleString("pt-BR", {
                            day: "2-digit", month: "2-digit", year: "numeric",
                            hour: "2-digit", minute: "2-digit",
                          });
                        } catch { dataExecucao = row.data_proxima_execucao; }
                      }
                      const situacaoBadge =
                        row.situacao === "P" ? (
                          <Badge bg="warning" text="dark">Pendente</Badge>
                        ) : row.situacao === "E" ? (
                          <Badge bg="success">Executado</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        );
                      return (
                        <tr key={idx}>
                          <td>{row.nome}</td>
                          <td>{getDescricaoPeriodicidade(row.periodicidade)}</td>
                          <td>{dataExecucao}</td>
                          <td>{situacaoBadge}</td>
                          <td>
                            {row.emails && row.emails.length > 0
                              ? row.emails.map((e, i) => <div key={i}>{e}</div>)
                              : <span className="text-muted">Sem e-mails</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-2">
                    <Pagination size="sm">
                      <Pagination.First onClick={() => setAgendamentosPage(1)} disabled={agendamentosPage === 1} />
                      <Pagination.Prev onClick={() => setAgendamentosPage((p) => Math.max(1, p - 1))} disabled={agendamentosPage === 1} />
                      {Array.from({ length: totalPages }, (_, i) => (
                        <Pagination.Item key={i + 1} active={agendamentosPage === i + 1} onClick={() => setAgendamentosPage(i + 1)}>
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next onClick={() => setAgendamentosPage((p) => Math.min(totalPages, p + 1))} disabled={agendamentosPage === totalPages} />
                      <Pagination.Last onClick={() => setAgendamentosPage(totalPages)} disabled={agendamentosPage === totalPages} />
                    </Pagination>
                  </div>
                )}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="modal-footer-padrao">
          <div className="modal-footer-acoes-esquerda" />
          <div className="modal-footer-acoes-direita">
            <Button variant="light" className="modal-footer-btn-cancelar" onClick={() => setShowAgendamentoModal(false)}>
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Estilo do botão clicável */}
      <style>{`
        .agendamento-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          cursor: pointer;
        }

        .agendamento-badge i {
          color: #0d6efd;
        }

        .agendamento-badge .badge {
          background-color: #0d6efd;
        }
      `}</style>
    </div>
  );
}

export default Documentos;
